import domready from "domready"
import "./style.css"
import spectral from "spectral.js"
import { voronoi } from "d3-voronoi"
import { polygonCentroid } from "d3-polygon"
import randomPalette, { randomPaletteWithBlack } from "./randomPalette"
import Color from "./Color"

const PHI = (1 + Math.sqrt(5)) / 2;
const TAU = Math.PI * 2;
const DEG2RAD_FACTOR = TAU / 360;

const config = {
    width: 0,
    height: 0,
    palette: []
};

/**
 * @type CanvasRenderingContext2D
 */
let ctx;
let canvas;

function relax(v, pts, count = 5)
{
    for (let i = 0; i < count; i++)
    {
        pts = v(pts).polygons().map(poly => {
            const c = polygonCentroid(poly)
            c[0] |= 0
            c[1] |= 0
            return c
        })
    }
    return pts
}

function drawPolygon(ctx, polygon)
{
    const last = polygon.length - 1
    const [x1, y1] = polygon[last]

    ctx.beginPath()
    ctx.moveTo(
        x1 | 0,
        y1 | 0
    )

    for (let i = 0; i < polygon.length; i++)
    {
        const [x1, y1] = polygon[i]
        ctx.lineTo(x1,y1)
    }
    ctx.fill()
    ctx.stroke()
}

const overdraw = 1.15


function createPoints(count = 20)
{
    const { width, height } = config

    const out = []

    const sq = count * count * 0.4
    for (let i = 0; i < sq; i++)
    {
        out.push([
            Math.random() * width | 0,
            Math.random() * height | 0
        ])

    }
    return out
}


function createColors(palette, polygonCount)
{
    const colors = []
    for (let i = 0; i < polygonCount; i++)
    {
        colors.push(palette[0 | Math.random() * palette.length])
    }
    return colors
}

function tempContext(width, height)
{
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    return canvas.getContext("2d")
}



function renderPolygons(polygons)
{
    const { width, height } = config

    const ctx = tempContext(width, height);

    polygons.map((poly,idx) => {

        const lo = idx & 255
        const hi = (idx >> 8)
        const color = new Color(0,hi,lo).toRGBHex()
        ctx.fillStyle = color
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        drawPolygon(ctx, poly)
    })

    return ctx.getImageData(0,0,width,height)
}


function getOtherCellIndex(index, edge)
{
    return edge.left.index === index ? edge.right.index : edge.left.index
}


function distance(x0, y0, x1, y1)
{
    const dx = x1 - x0
    const dy = y1 - y0
    return Math.sqrt(dx * dx + dy * dy)
}

function setSpectralJsColors(g, colors, count)
{
    const { length } = colors

    const last = length - 1
    const step = 1/(count * length - 1)

    let offset = 0
    for (let i = 0; i < last; i++)
    {
        const colorA = colors[i]
        const colorB = colors[i + 1]

        const pal = spectral.palette(colorA, colorB, count, spectral.HEX)
        for (let j = 0; j < pal.length; j++)
        {
            g.addColorStop(offset, pal[j])
            offset += step
        }
    }
}

function shuffle(a) {
    let j, x, i;
    for (i = a.length - 1; i > 0; i--) {
        j = Math.floor(Math.random() * (i + 1));
        x = a[i];
        a[i] = a[j];
        a[j] = x;
    }
    return a;
}



function randomColors(palette, num)
{
    let p = palette.slice()
    shuffle(p)

    while (p.length < num)
    {
        let p2
        do
        {
            p2 = palette.slice()
            shuffle(p2)
        } while(p[p.length-1] === p2[0])

        p = p.concat(p2)
    }

    return p.slice(0,num)
}


function rotate(x, y, w, h)
{
    const { width, height } = config

    ctx.save()

    ctx.beginPath()
    ctx.moveTo(x,y)
    ctx.lineTo(x + w,y)
    ctx.lineTo(x + w,y + h)
    ctx.lineTo(x,y + h)
    ctx.lineTo(x,y)
    ctx.clip()

    const mx = x + w/2
    const my = y + h/2

    ctx.translate(mx,my)
    ctx.rotate(TAU/4)
    ctx.translate(-mx,-my)

    ctx.drawImage(ctx.canvas, 0, 0, width, height)
    ctx.restore()


}


function coinFlip()
{
    return Math.random() < 0.5
}


function createCoinFlipMatrix(w, h)
{
    const count = w * h
    const out = []
    for (let i = 0; i < count; i++)
    {
        out.push(coinFlip())
    }
    return out
}

const dark = Color.from("#16161d")

function setShadowGradient(g,isCovered, max, min, off, size, color)
{
    const darkened = "#16161d"

    g.addColorStop(0,color)

    let prevCover = true
    const shadow = size * 0.15
    const offsetPerPixel = 1/max

    for (let i = 0; i < min; i++)
    {
        const cover = isCovered(i)
        if (cover !== prevCover)
        {
            const pos = off + i * size
            if (prevCover)
            {
                //g.addColorStop( (pos - shadow) * offsetPerPixel, darkened )
                g.addColorStop( (pos + shadow) * offsetPerPixel, color )
            }
            else
            {
                g.addColorStop( (pos - shadow/2) * offsetPerPixel, color )
                g.addColorStop( (pos + size/2) * offsetPerPixel, darkened )
                //g.addColorStop( (pos + size/2) * offsetPerPixel, darkened )
            }
            prevCover = cover
        }
    }
    g.addColorStop((max - off + shadow) *offsetPerPixel,color)
    return g
}


domready(
    () => {

        canvas = document.getElementById("screen");
        ctx = canvas.getContext("2d");

        const width = (window.innerWidth) | 0;
        const height = (window.innerHeight) | 0;

        config.width = width;
        config.height = height;

        canvas.width = width;
        canvas.height = height;

        const size = Math.max(width, height) / 10

        const paint = () => {

            const palette = randomPalette()

            config.palette = palette

            ctx.fillStyle = "#000"
            ctx.fillRect(0, 0, width, height)

            const cx = width >> 1
            const cy = height >> 1

            const size = Math.max(width,height)* 0.05

            const w = Math.floor(width / size - 2)
            const h = Math.floor(height / size - 2)

            const matrix = createCoinFlipMatrix(w,h)
            const get = (x,y) => matrix[y * w + x]

            const hColors = randomColors(palette, w)
            const vColors = randomColors(palette, h)

            const offX = (width - w * size) / 2
            const offY = (height - h * size) / 2

            for (let x = 0; x < hColors.length; x++)
            {
                const color = hColors[x]
                const x0 = offX + x * size
                ctx.fillStyle = setShadowGradient(
                    ctx.createLinearGradient(x0,0,x0,height),
                    y => get(x,y),
                    height,
                    h,
                    offY,
                    size,
                    color
                )
                ctx.fillRect(x0,0,size,height)
            }

            for (let y = 0; y < h; y++)
            {
                const color = vColors[y]
                const y0 = offY + y * size
                ctx.fillStyle = setShadowGradient(
                    ctx.createLinearGradient(0,y0,width,y0),
                    x => !get(x,y),
                    width,
                    w,
                    offX,
                    size,
                    color
                )

                let xStart = 0
                let prevState = true
                for (let x = 0; x < w; x++)
                {
                    const v = get(x,y)
                    if (prevState !== v)
                    {
                        const x0 = offX + x * size
                        if (prevState)
                        {
                            ctx.fillRect(xStart,offY + y * size, x0 - xStart, size)
                        }
                        else
                        {
                            xStart = x0
                        }
                        prevState = v
                    }
                }
                const xEnd = prevState ? xStart :  offX + w * size
                ctx.fillRect(xEnd, offY + y * size, (width - xEnd), size)
            }
        }
        paint()

        canvas.addEventListener("click", paint, true)
        // canvas.addEventListener("click", e => {
        //
        //     const tx = Math.floor(e.clientX / size)
        //     const ty = Math.floor(e.clientY / size)
        //
        //
        //     rotate(tx * size, ty* size, size, size)
        //
        //
        // }, true)
    }
);
